import { ref, onUnmounted } from "vue";
import { supabase } from "@/lib/supabase";
import {
  recordSessionEnd,
  getActiveSessions,
  SESSION_ID,
} from "@/services/sessionAnalytics";
import { auditLog, AUDIT_EVENTS } from "@/services/auditService";
import { generateFingerprint } from "@/utils/fingerprint";

const ABSOLUTE_TIMEOUT = 7 * 24 * 60 * 60 * 1000; // 7 days
const WARNING_BEFORE = 5 * 60 * 1000; // warn 5 min before expiry
const CHECK_INTERVAL = 5 * 60 * 1000; // check every 5 minutes
const MAX_FINGERPRINT_MISMATCHES = 3; // alert after this many mismatches

export function useSessionManager({
  store,
  onShowWarning,
  onHideWarning,
} = {}) {
  const sessionStart = ref(store?.state.session?.startedAt || Date.now());
  const warningShown = ref(false);
  const timeRemaining = ref(0);
  const fingerprintMismatches = ref(0);
  const loggingOut = ref(false);

  // Multi-tab sync: if another tab signs out (removes localStorage), log out here too
  const onStorageChange = (e) => {
    if (e.key === "elliotforwater-admin" && !e.newValue) {
      performLogout("other-tab");
    }
  };
  window.addEventListener("storage", onStorageChange);

  // Network recovery: revalidate session when coming back online
  const onOnline = async () => {
    store?.dispatch("logSessionEvent", { type: "NETWORK_RESTORED" });
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error || !session) {
      performLogout("network-invalid");
    } else {
      store?.dispatch("logSessionEvent", { type: "SESSION_REVALIDATED" });
    }
  };
  window.addEventListener("online", onOnline);

  const checkTimeout = async () => {
    const now = Date.now();
    const remaining = ABSOLUTE_TIMEOUT - (now - sessionStart.value);

    timeRemaining.value = Math.max(0, remaining);

    if (remaining <= 0) {
      performLogout("timeout");
      return;
    }

    if (remaining <= WARNING_BEFORE && !warningShown.value) {
      warningShown.value = true;
      store?.dispatch("logSessionEvent", {
        type: "SESSION_WARNING",
        meta: { remaining },
      });
      onShowWarning?.();
    }

    // Report significant events to anomaly detection Edge Function
    await reportEvent("SESSION_CHECK", store?.state.session?.fingerprint);

    // Concurrent session detection
    const userId = store?.state.user?.id;
    if (userId) {
      const active = await getActiveSessions(userId);
      const others = active.filter((s) => s.session_id !== SESSION_ID);
      if (others.length > 0) {
        store?.dispatch("logSessionEvent", {
          type: "CONCURRENT_SESSION",
          meta: { count: others.length },
        });
        store?.commit("SET_STATUS", {
          type: "error",
          message: `Your account is active in ${others.length} other session(s). Sign out of other devices if this wasn't you.`,
        });
      }
    }

    // Fingerprint mismatch detection
    if (store?.state.session?.fingerprint) {
      const current = generateFingerprint();
      if (current !== store.state.session.fingerprint) {
        fingerprintMismatches.value += 1;
        store?.dispatch("logSessionEvent", {
          type: "FINGERPRINT_MISMATCH",
          meta: { count: fingerprintMismatches.value },
        });

        await reportEvent("FINGERPRINT_MISMATCH", current);
        if (fingerprintMismatches.value >= MAX_FINGERPRINT_MISMATCHES) {
          store?.commit("SET_STATUS", {
            type: "error",
            message:
              "Unusual session activity detected. You have been signed out for your security.",
          });
          performLogout("fingerprint-mismatch");
        } else {
          store?.commit("SET_STATUS", {
            type: "error",
            message:
              "Unusual session activity detected. Please verify your identity.",
          });
        }
      }
    }
  };

  const intervalId = setInterval(checkTimeout, CHECK_INTERVAL);

  const extendSession = async () => {
    try {
      await supabase.auth.refreshSession();
      sessionStart.value = Date.now();
      store?.commit("SET_SESSION", { startedAt: sessionStart.value });
      store?.dispatch("logSessionEvent", { type: "SESSION_EXTENDED" });
      warningShown.value = false;
      onHideWarning?.();
    } catch (e) {
      if (process.env.NODE_ENV !== "production")
        console.warn("[session] refresh failed:", e.message);
    }
  };

  const performLogout = async (reason = "manual") => {
    loggingOut.value = true;
    clearInterval(intervalId);
    window.removeEventListener("storage", onStorageChange);
    window.removeEventListener("online", onOnline);

    try {
      await recordSessionEnd(reason);
    } catch (e) {
      /* ignore */
    }
    // scope:'local' ends only this admin session; the user's extension session
    // (separate origin, separate token) is intentionally left intact.
    const signOutError = await signOutWithTimeout("local");
    // Always clear locally and redirect regardless — supabase-js only removes the local session
    clearLocalSession();
    window.location.replace("/");
    if (signOutError) {
      auditLog(AUDIT_EVENTS.SIGN_OUT_FAILED, {
        reason: signOutError.message,
        context: "performLogout",
      });
    }
  };

  const validateSession = async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();
    if (error || !session) {
      store?.dispatch("logSessionEvent", { type: "SESSION_INVALID" });
      await performLogout("invalid");
      return false;
    }
    return true;
  };

  onUnmounted(() => {
    clearInterval(intervalId);
    window.removeEventListener("storage", onStorageChange);
    window.removeEventListener("online", onOnline);
  });

  return {
    extendSession,
    performLogout,
    validateSession,
    timeRemaining,
    warningShown,
    sessionStart,
    loggingOut,
  };
}

/**
 * Synchronous clear of session storage — safe for beforeunload, unlike async signOut().
 */
export function clearLocalSession() {
  const supabaseUrl = process.env.VUE_APP_SUPABASE_URL;
  if (supabaseUrl) {
    const projectRef = supabaseUrl.split(".")[0].split("//")[1];
    localStorage.removeItem(`sb-${projectRef}-auth-token`);
  }
  localStorage.removeItem("elliotforwater-admin");
  sessionStorage.removeItem("signing_in");
}

export async function signOutWithTimeout(scope, timeoutMs = 5000) {
  return Promise.race([
    supabase.auth.signOut({ scope }).then(({ error }) => error),
    new Promise((resolve) =>
      setTimeout(
        () => resolve(new Error(`signOut timed out after ${timeoutMs}ms`)),
        timeoutMs
      )
    ),
  ]);
}

async function reportEvent(event, fingerprint = null) {
  try {
    const {
      data: { session },
    } = await supabase.auth.getSession();
    if (!session) return;
    const url = `${process.env.VUE_APP_SUPABASE_URL}/functions/v1/session-monitor`;
    await fetch(url, {
      method: "POST",
      headers: {
        Authorization: `Bearer ${session.access_token}`,
        "Content-Type": "application/json",
      },
      body: JSON.stringify({ event, fingerprint }),
    });
  } catch (e) {
    if (process.env.NODE_ENV !== "production")
      console.warn("[session] reportEvent failed:", e.message);
  }
}
